import { Action, Button, CheckboxOption, Input, InputError, Spinner } from '@foodsby/nutrient'
import { Formik } from 'formik'
import React, { useEffect } from 'react'
import { boolean, object, string } from 'yup'

import { termsAndConditionsRoute } from '../../routes/routes'
import { stripEmoji } from '../../util/formatUtils'
import { pushSnowplow, toSignupProcessSchema } from '../../util/snowplow'

const schema = object().shape({
  TOS: boolean()
    .required('You must accept the Terms and Conditions Agreement')
    .oneOf([true], 'You must accept the Terms and Conditions Agreement'),
  password: string()
    .required('Password is required.')
    .min(8, 'Fudge 🍫! Your password must be at least 8 characters.'),
})

const CompleteAccountForm = ({
  errors,
  handleBlur,
  handleChange,
  handleSubmit,
  passwordShowing,
  submitLoading,
  touched,
  values,
}) => {
  useEffect(() => {
    if (touched.agreeToTOS && errors.agreeToTOS && !values.agreeToTOS)
      pushSnowplow(
        toSignupProcessSchema('sign_up', 'user_terms_toggle', values.agreeToTOS, errors.agreeToTOS),
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched.agreeToTOS && errors.agreeToTOS])

  return (
    <form onSubmit={handleSubmit}>
      <div className="row start-xs">
        <div className="col-xs-12 _lr-hide">
          <Input
            className="confirmPassword"
            id="completeAccountPassword"
            isValid={!errors.password}
            label="Create Password"
            name="password"
            onBlur={event => {
              handleBlur(event)
              pushSnowplow(
                toSignupProcessSchema(
                  'sign_up',
                  'user_password_confirm',
                  null,
                  stripEmoji(errors.password),
                ),
              )
            }}
            onChange={handleChange}
            tabIndex="2"
            type={passwordShowing ? 'text' : 'password'}
            value={values.password}
          />
          <InputError>{touched.password && errors.password}</InputError>
        </div>
        <div className="col-xs-12 checkbox">
          <CheckboxOption
            checked={values.checkbox}
            id="checkbox"
            label={
              <small>
                By signing up, you agree to our{' '}
                <a
                  href={termsAndConditionsRoute.path}
                  onClick={() => pushSnowplow(toSignupProcessSchema('sign_up', 'user_terms_view'))}
                  rel="noopener noreferrer"
                  style={{ display: 'inline' }}
                  target="_blank"
                >
                  <small>Terms and Conditions Agreement</small>
                </a>
              </small>
            }
            name="TOS"
            onBlur={handleBlur}
            onChange={event => {
              handleChange(event)
              pushSnowplow(
                toSignupProcessSchema('sign_up', 'user_terms_toggle', event.target.checked),
              )
            }}
            required
            type="checkbox"
          />
          <InputError>{touched.TOS && errors.TOS}</InputError>
        </div>
        <div className="col-xs-12 center-xs">
          <Button
            disabled={submitLoading || errors.password || errors.TOS}
            style={{ marginTop: '16px' }}
            type="submit"
          >
            {submitLoading && <Spinner className="spinner-xs" />} Submit
          </Button>
        </div>
      </div>
    </form>
  )
}

const CompleteAccount = ({
  errorMessage,
  handleSubmit,
  passwordShowing,
  submitLoading,
  successMessage,
  togglePasswordVisibility,
  username,
}) => {
  useEffect(() => {
    pushSnowplow(toSignupProcessSchema('sign_up', 'user_view'))
  }, [])
  useEffect(() => {
    if (errorMessage) {
      pushSnowplow(toSignupProcessSchema('sign_up', 'form_submit', 'failure', errorMessage))
    }
    if (passwordShowing) {
      pushSnowplow(toSignupProcessSchema('sign_up', 'show_password'))
    }
  }, [errorMessage, passwordShowing])
  return (
    <div className="row segment">
      <div className="col-xs-12">
        <label>Email:</label>
        <p className="segment-text">{username}</p>
        <div className="col end-xs" style={{ marginBottom: '10px' }}>
          <Action onClick={togglePasswordVisibility} style={{ fontSize: '15px' }}>
            {passwordShowing ? 'Hide Pasword' : 'Show Password'}
          </Action>
        </div>
        <Formik
          gtm-data-elm-id="CompleteAccountform"
          initialValues={{
            TOS: false,
            password: '',
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
            <CompleteAccountForm
              errors={errors}
              handleBlur={handleBlur}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              passwordShowing={passwordShowing}
              submitLoading={submitLoading}
              touched={touched}
              values={values}
            />
          )}
        </Formik>
        <div className="success">{successMessage}</div>
        <div className="error">{errorMessage} </div>
        {errorMessage && (
          <div>
            Already have an account?
            <br />
            Please try logging in <a href="/login">here</a>
          </div>
        )}{' '}
      </div>
    </div>
  )
}

export default CompleteAccount
